import axios from "axios";

/**
 * Silent session keep-alive (June 2026).
 *
 * Admins uploading large resource files were losing their work when the 72h JWT
 * lapsed mid-session. This quietly swaps the token for a fresh one well before
 * it expires — no prompts, no interruption. If the refresh fails we do nothing:
 * the normal expiry path (authInterceptor) still handles a genuinely dead token.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHECK_INTERVAL_MS = 15 * 60 * 1000; // re-check every 15 min
const REFRESH_WHEN_HOURS_LEFT = 12; // refresh once less than 12h remains
const TOKEN_KEYS = ["app_token", "admin_token"];

let timer = null;
let inFlight = false;

function decodeExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function hoursUntilExpiry(token) {
  const exp = token && decodeExp(token);
  if (!exp) return null;
  return (exp - Date.now()) / 3600000;
}

/** Refresh now if the token is valid but nearing expiry. Returns the token in use. */
export async function ensureFreshToken({ force = false } = {}) {
  const token = localStorage.getItem("app_token") || localStorage.getItem("admin_token");
  if (!token || inFlight) return token;

  const hoursLeft = hoursUntilExpiry(token);
  if (hoursLeft === null || hoursLeft <= 0) return token; // dead or unreadable — leave it to the interceptor
  if (!force && hoursLeft > REFRESH_WHEN_HOURS_LEFT) return token;

  inFlight = true;
  try {
    const res = await axios.post(`${API}/auth/refresh-token`, null, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const fresh = res.data?.token;
    if (!fresh) return token;
    // Replace every key that currently holds a token (admin_token mirrors
    // app_token for elevated SSO users).
    TOKEN_KEYS.forEach((k) => {
      if (localStorage.getItem(k)) localStorage.setItem(k, fresh);
    });
    return fresh;
  } catch {
    return token;
  } finally {
    inFlight = false;
  }
}

export function startSessionKeepAlive() {
  if (timer) return;
  ensureFreshToken();
  timer = setInterval(ensureFreshToken, CHECK_INTERVAL_MS);
  // Coming back to a tab that sat idle overnight is the riskiest moment
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") ensureFreshToken();
  });
}
